import { takeLatest, takeEvery, put, all, delay, call, select } from "redux-saga/effects";
import queryString from "query-string";
import { toast } from "react-toastify";
import { getTracks,
    setBackDropLoader,
    setSelectedTrackerTasks
} from "modules/Tracker/services/Tracks/actions";
import { getErrorMessage } from "utils/sagasErrorHandler";
import { TRACKER_GENERAL_FILTER } from "modules/Tracker/constants";
import { ITEMS_PER_PAGE } from "constants/general";
import { statusTypeSelector } from "services/StatusType/selectors";
import { sharedHistory } from "routes/sharedHistory";
import { ROUTES_MAP } from "routes/constants";
import { CONFIG_MANAGER_LIGHT_STEPS } from "modules/ConfigManagerLite";
import {
    getChildrenTrackRequest,
    getHttpResponseRequest,
    getObjectStructureRequest,
    getStatusHistoryRequest,
    getTemplateChildrenRequest,
    getTrackRequest,
    stopTrackRequest,
    getHttpRequestRequest,
    getTracksCountRequest,
    getScrapeAttemptsRequest,
    exportTrackResultObjectsRequest,
    pidProcessRequest,
    getMetadataRequest, getResultObjectsRequest
} from "modules/Tracker/services/Actions/api";
import {
    getViewMetadata,
    setMetadata,
    stopTrackAction,
    getViewResults,
    setViewResultsDialog,
    mergeViewResultsDialog,
    getSourceResults,
    setSourceResultsDialog,
    mergeSourceResultsDialog,
    getStatusHistory,
    setStatusHistoryDialog,
    mergeStatusHistoryDialog,
    getTrackerTree,
    setTrackTree,
    mergeTrackTree,
    exportResultObjects,
    downloadHttpResponseContentToFile,
    checkSourceTrack,
    getViewScrapeAttempts,
    mergeViewScrapeAttemptsDialog,
    setViewScrapeAttemptsDialog,
    mergeDownloadResultObjectsDialog,
    rescrapeTrackAction,
    resendScrapedResultsAction
} from "modules/Tracker/services/Actions/actions";
import {downloadDialogSelector} from "modules/Tracker/services/Actions/selectors";
import {sidParamsSelector} from "modules/App/state/selectors";
import {oneTimeSearchRequest, resendScrapedResultsRequest} from "modules/Scheduler/services/InitialScrapeTasks/api";

//Workers
function* handleRefreshScrapeTasks() {
    yield put(setSelectedTrackerTasks([]));
    const sidParams = yield select(sidParamsSelector);
    yield put(getTracks({ limit: ITEMS_PER_PAGE, ...sidParams }));
}

function* stopTrackWorker({ payload }) {
    try {
        yield call(stopTrackRequest, payload);
        yield handleRefreshScrapeTasks();
    } catch (error) {
        getErrorMessage(error);
    }
}

function* rescrapeTrackWorker({ payload }) {
    try {
        yield call(oneTimeSearchRequest, {
            branch_code: payload.branch_code,
            brand_code: payload.brand_code,
            debug_mode: payload.debug_mode,
            sandbox_mode: payload.sandbox_mode,
            save_to_hot_storage: payload.save_to_hot_storage,
            store_http_response: payload.store_http_response,
            disable_cache: payload.disable_cache,
            image_url: payload.image_url,
            keyword: payload.keyword,
            receiver_code: payload.track_id.receiver_code,
            source_code: payload.track_id.source_code,
            url: payload.http_request.url,
            ab_test: payload.ab_test ? payload.ab_test : null,
        });
        yield call(
            toast.success,
            `The task has been successfully sent to rescrape`
        );
    } catch (error) {
        getErrorMessage(error);
    }
}

function* resendScrapedResultsWorker({ payload }) {
    try {
        yield call(resendScrapedResultsRequest, payload);
        yield call(
            toast.success,
            `Scraped results have been successfully resent`
        );
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleGetResultObjectsRequest(data) {
    try {
        return yield call(getResultObjectsRequest, data);
    }catch (error){
        getErrorMessage(error);
        return null;
    }
}

function* handleGetObjectStructureRequest(receiver_code) {
    try {
        return yield call(getObjectStructureRequest, receiver_code);
    }catch (error){
        getErrorMessage(error);
        return null;
    }
}

function* handleGetViewResults({ payload }) {
    try {
        const { task, params } = payload;
        const { track_id: { track_id, initial_track_id, receiver_code }, result_objects } = task;
        const { storage_type_code } = params;

        if (storage_type_code === "COLD") {
            const requestParams = { ...params, initial_track_id };
            yield put(setViewResultsDialog({
                id: track_id,
                isOpen: true,
                isLoading: true
            }));
            const [ resultObjectsResponse, objectStructureResponse ] = yield all([
                handleGetResultObjectsRequest({ initialTrackId: track_id, filterParams: requestParams }),
                handleGetObjectStructureRequest(receiver_code)
            ]);
            let scraped_result_objects = [];
            if (resultObjectsResponse) {
                const { result_objects } = resultObjectsResponse;
                scraped_result_objects = result_objects;
            }
            if (scraped_result_objects.length > 0) {
                yield put(mergeViewResultsDialog({
                    isLoading: false,
                    isOpen: true,
                    object_structures: objectStructureResponse,
                    scraped_result_objects: scraped_result_objects,
                    id: track_id,
                }));
            } else {
                yield put(mergeViewResultsDialog({
                    isLoading: false,
                    isOpen: false,
                    id: track_id,
                }));
            }
        } else {
            const objectStructure = yield call(getObjectStructureRequest, receiver_code);
            yield put(mergeViewResultsDialog({
                isLoading: false,
                isOpen: true,
                object_structures: objectStructure,
                scraped_result_objects: result_objects,
                id: track_id,
            }));
        }
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleGetViewScrapeAttempts({ payload }) {
    try {
        const { task, params } = payload;
        const { track_id: { track_id, initial_track_id } } = task;

        const requestParams = { ...params, initial_track_id };
        yield put(setViewScrapeAttemptsDialog({
            id: track_id,
            isOpen: true,
            isLoading: true
        }));
        const { scrape_attempts } = yield call(getScrapeAttemptsRequest, { initialTrackId: track_id, filterParams: requestParams });
        yield put(mergeViewScrapeAttemptsDialog({ isLoading: false, scrapeAttempts: scrape_attempts }));
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleGetViewMetadata({ payload }) {
    try {
        const { task, params } = payload;
        const { track_id: { track_id, initial_track_id } } = task;
        const { storage_type_code } = params;

        if (storage_type_code === "COLD") {
            const requestParams = { ...params, initial_track_id };
            yield put(setMetadata({
                track_id: track_id,
                isOpen: true,
                isLoading: true
            }));
            const metadata = yield call(getMetadataRequest, { initialTrackId: track_id, filterParams: requestParams });
            yield put(setMetadata({ isLoading: false, isOpen: true, metadata: metadata, track_id: track_id }));
        } else {
            yield put(setMetadata({ isLoading: false, isOpen: true, metadata: task.metadata, track_id: track_id }));
        }
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleHttpResponseRequest(data) {
    try {
        return yield call(getHttpResponseRequest, data);
    }catch (error){
        getErrorMessage(error);
        return null;
    }
}

function* handleHttpRequestRequest(data) {
    try {
        return yield call(getHttpRequestRequest, data);
    }catch (error){
        getErrorMessage(error);
        return null;
    }
}

function* handleGetSourceResults({ payload }) {
    try {
        const { task, params } = payload;
        const { track_id: { track_id, initial_track_id } } = task;

        const requestParams = { ...params, initial_track_id };

        yield put(setSourceResultsDialog({ id: track_id, isOpen: true, isLoading: true }));

        const [ sourceHttpResponse, trimJavascript, sourceHttpRequest ] = yield all([
            handleHttpResponseRequest({ initialTrackId: track_id, filterParams: requestParams }),
            handleHttpResponseRequest({ initialTrackId: track_id, filterParams: { ...requestParams, trim_javascript: true, use_absolute_path: true } }),
            handleHttpRequestRequest({ initialTrackId: track_id, filterParams: requestParams })
        ]);

        const sourceTrack = {
            sourceHttpResponse,
            trimJavascript,
            sourceHttpRequest,
            task
        };

        yield put(mergeSourceResultsDialog({ isLoading: false, sourceTrack }));
    } catch (error) {
        yield put(mergeSourceResultsDialog({ isLoading: false, sourceTrack: {} }));
        getErrorMessage(error);
    }
}

function* handleDownloadHttpResponseContentToFile({ payload }) {
    try {
        const { task, params } = payload;
        const { track_id: { track_id, initial_track_id } } = task;

        const requestParams = { ...params, initial_track_id };
        const sourceHttpResponse = yield call(getHttpResponseRequest, { initialTrackId: track_id, filterParams: requestParams });

        let stringValue = sourceHttpResponse?.http_response_content;
        if (stringValue.length === 0) {
            return
        }
        let blob = new Blob([stringValue], { type: 'text/plain' });
        let a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = `${track_id}.txt`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleGetStatusHistory({ payload }) {
    try {
        const { track_id: { track_id } } = payload;
        yield put(setStatusHistoryDialog({
            id: track_id,
            isOpen: true,
            isLoading: true
        }));

        const history = yield call(getStatusHistoryRequest, track_id);
        yield put(mergeStatusHistoryDialog({ isLoading: false, history }));
    } catch (error) {
        getErrorMessage(error);
    }
}

function* statusTypeTracks({ statusTypes, ...searchParams }) {
    try {
        return yield all(Object.values(statusTypes).map(statusType => call(getTemplateChildrenRequest, {
            status_type_code: statusType.code,
            ...searchParams
        })));
    } catch (error) {
        getErrorMessage(error);
    }
}

function* getChildrenTrack(filterParams, data = []) {
    try {
        const LIMIT = filterParams.limit || ITEMS_PER_PAGE.limit;
        const { scrape_tasks } = yield call(getChildrenTrackRequest, filterParams);
        const newData = [ ...data, ...scrape_tasks ];

        if(scrape_tasks.length === Number(LIMIT)) {
            const newFilterParams = { ...filterParams };
            newFilterParams.offset = Number(newFilterParams.offset) + Number(LIMIT);

            return yield getChildrenTrack(newFilterParams, newData);
        }

        return newData;
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleGetTrackerTree({ payload }) {
    try {
        yield put(setTrackTree({ isLoading: true }));

        const { params: { initialTrackId, trackID }, searchParams } = payload;
        const { statusTypes } = yield select(statusTypeSelector);

        let trackTree = {};

        const { scrape_tasks } = yield call(getTrackRequest, { initialTrackId, trackID, searchParams });
        const initialParent = scrape_tasks[0].track_id.parent_track_id;

        //Current data
        const { count } = yield call(getTracksCountRequest, { parentTrackId: trackID, initialTrackId, searchParams });
        const statusType = yield call(
            statusTypeTracks,
            {
                limit: "0",
                initial_track_id: scrape_tasks[0].track_id.track_id,
                parent_track_id: scrape_tasks[0].track_id.parent_track_id || scrape_tasks[0].track_id.track_id,
                storage_type_code: searchParams.storage_type_code,
                statusTypes
            }
        );

        trackTree.current = { ...scrape_tasks[0] };
        trackTree.current.url = scrape_tasks[0].url;
        trackTree.current.message = scrape_tasks[0].message;
        trackTree.current.actual_template_code = scrape_tasks[0].actual_template_code;
        trackTree.current.count = count || 0;
        trackTree.current.track_id = trackID;
        trackTree.current.initial_track_id = initialTrackId;
        trackTree.current.statusType = statusType;

        //Parent data
        if(initialParent){
            const parentCountTrack = yield call(getTracksCountRequest, { initialTrackId, parentTrackId: initialParent, searchParams });
            const { scrape_tasks } = yield call(getTrackRequest, { initialTrackId, trackID: initialParent, searchParams });

            trackTree.parent = {};
            trackTree.parent.item = scrape_tasks[0];
            trackTree.parent.count = parentCountTrack.count;
            trackTree.parent.statusType = yield call(statusTypeTracks, {
                limit: "0",
                initial_track_id: initialTrackId,
                parent_track_id: initialParent,
                storage_type_code: searchParams.storage_type_code,
                statusTypes
            });
        }

        //Children data
        trackTree.children = yield call(getChildrenTrack, {
            ...TRACKER_GENERAL_FILTER,
            initial_track_id: initialTrackId,
            parent_track_id: trackID,
            ...searchParams
        });

        yield put(mergeTrackTree({ trackTree: trackTree, isLoading: false }));
    } catch (error) {
        getErrorMessage(error);
        yield put(setTrackTree());
    }
}

function* handleCheckSourceTrack({ payload }){
    try {
        yield put(setBackDropLoader(true));
        const { task, params } = payload;
        const { track_id: { initial_track_id, track_id, receiver_code }, branch_code } = task;
        const requestParams = { ...params, initial_track_id };

        const { http_response_content } = yield call(
            getHttpResponseRequest,
            { initialTrackId: track_id, filterParams: { ...requestParams, trim_javascript: true, use_absolute_path: true } }
        );

        if(
            http_response_content && (
                http_response_content.indexOf("!DOCTYPE html>") !== -1 ||
                http_response_content.indexOf("!doctype html>") !== -1 ||
                http_response_content.indexOf("<html") !== -1
            )
        ) {
            const searchParams = {
                initial_track_id,
                track_id,
                branch_codes: branch_code,
                receiver_code,
                ...params
            };

            sharedHistory.push(
                `${ROUTES_MAP.configManagerLite.path}/${CONFIG_MANAGER_LIGHT_STEPS.two}?${queryString.stringify(searchParams)}`
            );
        } else {
            yield call(toast.error ,"The Track does not have http response content");
        }

        yield put(setBackDropLoader(false));
    } catch (error) {
        yield put(setBackDropLoader(false));
        getErrorMessage(error);
    }
}

function* getAllResultObjects (pid) {
    try {
        const { isOpen } = yield select(downloadDialogSelector);

        const value = yield call(pidProcessRequest, pid);
        const status = Number((value * 100).toFixed());
        yield put(mergeDownloadResultObjectsDialog({ isLoading: true, status }));
        if (status >= 99) {
            yield delay(4000);
        }
        if(status !== 100 && isOpen) {
            yield delay(1500);
            yield getAllResultObjects(pid);
        } else {
            yield put(mergeDownloadResultObjectsDialog({ isLoading: false, status: null }));
        }
    } catch (error) {
        getErrorMessage(error);
        yield put(mergeDownloadResultObjectsDialog({ isLoading: false }));
    }
}

function* handleExportResultObjects ({ payload }) {
    try {
        yield put(mergeDownloadResultObjectsDialog({ isOpen: true, isLoading: true, status: 0, url: "" }));
        const { pid, url } = yield call(exportTrackResultObjectsRequest, payload);
        yield put(mergeDownloadResultObjectsDialog({ isOpen: true, isLoading: true, status: 0, url }));
        yield getAllResultObjects(pid, url);
    } catch (error) {
        getErrorMessage(error);
    }
}

// Watchers
export function* rootSagaActions() {
    yield all([
        yield takeLatest(stopTrackAction, stopTrackWorker),
        yield takeLatest(rescrapeTrackAction, rescrapeTrackWorker),
        yield takeLatest(resendScrapedResultsAction, resendScrapedResultsWorker),
        yield takeLatest(getViewResults, handleGetViewResults),
        yield takeLatest(getViewScrapeAttempts, handleGetViewScrapeAttempts),
        yield takeLatest(getViewMetadata, handleGetViewMetadata),
        yield takeLatest(getSourceResults, handleGetSourceResults),
        yield takeLatest(getStatusHistory, handleGetStatusHistory),
        yield takeLatest(getTrackerTree, handleGetTrackerTree),
        yield takeEvery(checkSourceTrack, handleCheckSourceTrack),
        yield takeLatest(exportResultObjects, handleExportResultObjects),
        yield takeLatest(downloadHttpResponseContentToFile, handleDownloadHttpResponseContentToFile)
    ]);
}
